import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Navigation.css';
import logo from '../assets/murron-logo-wordmark.png';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../locales/translations';
import { MODULES } from '../data/modules';

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [aboutUsOpen, setAboutUsOpen] = useState(false);
  const [whatWeDoOpen, setWhatWeDoOpen] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 关闭移动端菜单当路由改变时
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // 防止移动端菜单打开时页面滚动
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // 「我们的服务」下拉：四大交付模块，内容统一来自 src/data/modules.js
  const whatWeDoSections = MODULES.map((m) => ({
    path: `/services/${m.id}`,
    code: `${m.number} · ${m[language].code}`,
    title: m[language].navTitle
  }));

  const insightsSections = [
    t.nav.insightsSubmenu.section1,
    t.nav.insightsSubmenu.section2,
    t.nav.insightsSubmenu.section3,
    t.nav.insightsSubmenu.section4
  ];

  const aboutUsSubmenu = [
    { path: '/about#why-murron', label: t.nav.aboutUsSubmenu.whyMurron },
    { path: '/about#why-hong-kong', label: t.nav.aboutUsSubmenu.whyHongKong },
    { path: '/about#how-we-work', label: t.nav.aboutUsSubmenu.howWeWork },
    { path: '/meet-our-team', label: t.nav.aboutUsSubmenu.founder },
    { path: '/about#connect', label: t.nav.aboutUsSubmenu.connectWithUs }
  ];

  return (
    <>
      <nav className={`navigation ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <Link to="/" className="nav-logo">
            <img src={logo} alt="MuRRÓN Logo" className="logo-image" />
            <div className="logo-text-container">
              <span className="logo-text">昭世家族办公室</span>
              <span className="logo-en">MuRRÓN Family Office</span>
            </div>
          </Link>
          
          {/* 移动端菜单按钮 */}
          <button 
            className="mobile-menu-toggle" 
            onClick={toggleMobileMenu}
            aria-label="Toggle Menu"
          >
            {mobileMenuOpen ? '✕' : '☰'}
          </button>

          <ul className={`nav-menu ${mobileMenuOpen ? 'active' : ''}`}>
            <li 
              className="nav-dropdown"
              onMouseEnter={() => setWhatWeDoOpen(true)}
              onMouseLeave={() => setWhatWeDoOpen(false)}
            >
              <span className="nav-dropdown-trigger">
                {t.nav.whatWeDo}
              </span>
              <div className={`nav-dropdown-menu nav-dropdown-services ${whatWeDoOpen ? 'active' : ''}`}>
                <div className="nav-dropdown-content">
                  <div className="nav-dropdown-intro">
                    <h3 className="nav-dropdown-title">{t.nav.whatWeDoSubmenu.title}</h3>
                    <Link to="/services" className="nav-dropdown-overview-link">{t.nav.whatWeDoSubmenu.overview}</Link>
                  </div>
                  <div className="nav-dropdown-sections">
                    {whatWeDoSections.map((section) => (
                      <div key={section.path} className="nav-dropdown-section">
                        <Link to={section.path} className="nav-dropdown-section-title-link">
                          <span className="nav-dropdown-category">{section.code}</span>
                          <h4 className="nav-dropdown-section-title">{section.title}</h4>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </li>
            <li 
              className="nav-dropdown"
              onMouseEnter={() => setInsightsOpen(true)}
              onMouseLeave={() => setInsightsOpen(false)}
            >
              <span className="nav-dropdown-trigger">
                {t.nav.insights}
              </span>
              <div className={`nav-dropdown-menu ${insightsOpen ? 'active' : ''}`}>
                <div className="nav-dropdown-content">
                  <div className="nav-dropdown-intro">
                    <h3 className="nav-dropdown-title">{t.nav.insightsSubmenu.title}</h3>
                    <p className="nav-dropdown-description">{t.nav.insightsSubmenu.description}</p>
                  </div>
                  <div className="nav-dropdown-simple-list">
                    {insightsSections.map((section, index) => (
                      <Link key={index} to={section.path} className="nav-dropdown-simple-link">
                        {section.title}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </li>
            <li 
              className="nav-dropdown"
              onMouseEnter={() => setAboutUsOpen(true)}
              onMouseLeave={() => setAboutUsOpen(false)}
            >
              <span className="nav-dropdown-trigger">
                {t.nav.aboutUs}
              </span>
              <div className={`nav-dropdown-menu ${aboutUsOpen ? 'active' : ''}`}>
                <div className="nav-dropdown-content">
                  <div className="nav-dropdown-intro">
                    <h3 className="nav-dropdown-title">{t.nav.aboutUsSubmenu.title}</h3>
                    <p className="nav-dropdown-description">{t.nav.aboutUsSubmenu.description}</p>
                  </div>
                  <div className="nav-dropdown-simple-list">
                    {aboutUsSubmenu.map((item, index) => (
                      <Link key={index} to={item.path} className="nav-dropdown-simple-link">
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </li>
            <li>
              <a 
                href="https://www.murron-omni.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="nav-link nav-link-external"
              >
                OMNI
              </a>
            </li>
            <li>
              <button 
                className="language-toggle" 
                onClick={toggleLanguage}
                aria-label="Toggle Language"
              >
                {language === 'zh' ? 'EN' : '中文'}
              </button>
            </li>
          </ul>
        </div>
      </nav>

      {/* 移动端遮罩层 */}
      <div 
        className={`mobile-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={toggleMobileMenu}
      />
    </>
  );
};

export default Navigation;
